import React, { useEffect, useRef, useState } from 'react';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getFile } from '@opu/utils';
import AxiosInstance from '../../helpers/AxiosInstance';
import './UdlChatbot.css';

const FAQ = [
  { id: 'search', keys: ['search', 'chercher', 'rechercher', 'trouver', 'كتاب', 'بحث'] },
  { id: 'login', keys: ['login', 'connexion', 'connecter', 'sign in', 'تسجيل'] },
  { id: 'subscribe', keys: ['subscribe', 'abonnement', 'progres', 'اشتراك'] },
  { id: 'buy', keys: ['buy', 'purchase', 'acheter', 'شراء'] },
  { id: 'contact', keys: ['contact', 'email', 'phone', 'اتصال', 'هاتف'] },
];

function extractItems(payload) {
  if (!payload) return [];
  if (Array.isArray(payload.items)) return payload.items;
  if (Array.isArray(payload.data?.items)) return payload.data.items;
  if (Array.isArray(payload.data)) return payload.data;
  return [];
}

export default function UdlChatbot() {
  const { t, i18n } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const logRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [awaitingSearch, setAwaitingSearch] = useState(false);
  const [messages, setMessages] = useState([]);

  const hidden = (location.pathname || '').includes('/view-book');

  useEffect(() => {
    setMessages([{ id: 'welcome', role: 'bot', text: t('chatbot.welcome') }]);
  }, [t, i18n.language]);

  useEffect(() => {
    if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight;
  }, [messages, busy, open]);

  if (hidden) return null;

  const push = (msg) => setMessages((prev) => [...prev, { id: `${Date.now()}-${Math.random()}`, ...msg }]);

  const matchFaq = (text) => {
    const n = (text || '').toLowerCase();
    let best = null;
    let score = 0;
    FAQ.forEach((item) => {
      let s = 0;
      item.keys.forEach((k) => {
        if (n.indexOf(k) !== -1) s += Math.max(2, k.length);
      });
      if (s > score) {
        score = s;
        best = item.id;
      }
    });
    return score >= 2 ? best : null;
  };

  const searchBooks = async (query) => {
    const q = (query || '').trim();
    if (!q) return;
    push({ role: 'bot', text: t('chatbot.searching', { query: q }) });
    setBusy(true);
    try {
      const payload = await AxiosInstance.get(
        `/book/public?page=0&limit=5&keyword=${encodeURIComponent(q)}`
      );
      const books = extractItems(payload).slice(0, 5);
      if (!books.length) {
        push({
          role: 'bot',
          text: t('chatbot.none', { query: q }),
          links: [{ label: t('chatbot.catalog'), href: '/books' }],
        });
      } else {
        push({
          role: 'bot',
          text: t('chatbot.found', { count: books.length }),
          books,
          links: [{ label: t('chatbot.seeAll'), href: `/books?keyword=${encodeURIComponent(q)}` }],
        });
      }
    } catch (err) {
      console.error('[UDL chatbot] search failed', err);
      push({
        role: 'bot',
        text: t('chatbot.replies.error'),
        links: [{ label: t('chatbot.catalog'), href: '/books' }],
      });
    } finally {
      setBusy(false);
    }
  };

  const handleFaq = (id) => {
    if (id === 'search') {
      setAwaitingSearch(true);
      push({ role: 'bot', text: t('chatbot.replies.askSearch') });
      return;
    }
    setAwaitingSearch(false);
    const href =
      id === 'login'
        ? '/login'
        : id === 'subscribe'
          ? 'https://progres.mesrs.dz/epaiement/epaiementOpu.xhtml'
          : id === 'buy'
            ? '/visitor/books'
            : '/contact';
    push({
      role: 'bot',
      text: t(`chatbot.replies.${id}`),
      links: [{ label: t(`chatbot.chips.${id}`), href }],
    });
  };

  const ask = (raw) => {
    const q = (raw || '').trim();
    if (!q || busy) return;
    push({ role: 'user', text: q });
    setDraft('');
    if (awaitingSearch) {
      setAwaitingSearch(false);
      searchBooks(q);
      return;
    }
    const faq = matchFaq(q);
    if (faq && faq !== 'search') {
      handleFaq(faq);
      return;
    }
    searchBooks(q);
  };

  const go = (href) => {
    if (!href) return;
    if (/^https?:/i.test(href)) {
      window.open(href, '_blank', 'noopener');
      return;
    }
    setOpen(false);
    history.push(href);
  };

  const chips = ['search', 'login', 'subscribe', 'buy', 'contact'];

  return (
    <div className={`udl-chat${open ? ' is-open' : ''}`}>
      <button
        type="button"
        className="udl-chat__fab"
        aria-expanded={open}
        aria-controls="udl-chat-panel"
        onClick={() => setOpen((v) => !v)}
      >
        <span aria-hidden="true">💬</span>
        <span className="udl-chat__sr">{t('chatbot.open')}</span>
      </button>
      <div id="udl-chat-panel" className="udl-chat__panel" hidden={!open}>
        <div className="udl-chat__head">
          <strong>{t('chatbot.title')}</strong>
          <button type="button" className="udl-chat__x" aria-label={t('chatbot.close')} onClick={() => setOpen(false)}>
            ×
          </button>
        </div>
        <div className="udl-chat__log" role="log" ref={logRef}>
          {messages.map((msg) => (
            <div key={msg.id} className={`udl-chat__msg udl-chat__msg--${msg.role}`}>
              <p>{msg.text}</p>
              {msg.books?.length ? (
                <ul className="udl-chat__books">
                  {msg.books.map((book) => (
                    <li key={book.id}>
                      <button type="button" className="udl-chat__book" onClick={() => go(`/book-details/${book.id}`)}>
                        <img
                          src={book.cover ? getFile('book', book.cover) : '/assets/images/book_default.png'}
                          alt=""
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = '/assets/images/book_default.png';
                          }}
                        />
                        <span>{book.title}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              ) : null}
              {msg.links?.map((link) =>
                /^https?:/i.test(link.href) ? (
                  <a key={link.href} className="udl-chat__link" href={link.href} target="_blank" rel="noopener noreferrer">
                    {link.label}
                  </a>
                ) : (
                  <Link key={link.href} className="udl-chat__link" to={link.href} onClick={() => setOpen(false)}>
                    {link.label}
                  </Link>
                )
              )}
            </div>
          ))}
          {busy ? <div className="udl-chat__msg udl-chat__msg--bot">{t('chatbot.thinking')}</div> : null}
        </div>
        <div className="udl-chat__chips">
          {chips.map((id) => (
            <button
              key={id}
              type="button"
              onClick={() => {
                push({ role: 'user', text: t(`chatbot.chips.${id}`) });
                handleFaq(id);
              }}
            >
              {t(`chatbot.chips.${id}`)}
            </button>
          ))}
        </div>
        <form
          className="udl-chat__form"
          onSubmit={(e) => {
            e.preventDefault();
            ask(draft);
          }}
        >
          <label className="udl-chat__sr" htmlFor="udl-chat-q">
            {t('chatbot.placeholder')}
          </label>
          <input
            id="udl-chat-q"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder={t('chatbot.placeholder')}
            autoComplete="off"
          />
          <button type="submit" disabled={busy}>
            {t('chatbot.send')}
          </button>
        </form>
      </div>
    </div>
  );
}
